interface NestedObject<T> {
  [key: string]: T | NestedObject<T>;
}

interface Map {
  [key: string]: object
}

type FilterTarget = Array<object> | object
type FilterTree = NestedObject<boolean>;
type NullableFilterTarget = FilterTarget | null | undefined;

/**
 * Filters object keys (properties) of the given `input`
 * by the given `whitelistedKeysTree`, recursively. The
 * output is a new object without any reference to `input`.
 * 
 * Example:
 * `input`:
 * ```JavaScript
 * { a: '1', b: '2' c: { d: 3, e: 4 } }
 * ```
 * `whitelistedKeysTree`:
 * ```JavaScript
 * { b: true, c: { d: true } }
 * ```
 * *Result*:
 * ```JavaScript
 * { b: '2' c: { d: 3 } }
 * ```
 * 
 * @param {FilterTarget} input 
 * @param {FilterTree} whitelistedKeysTree 
 * @returns filtered input
 */
export function filterKeysRecursive(input: NullableFilterTarget, whitelistedKeysTree: FilterTree): NullableFilterTarget {
  if (!input || typeof input !== 'object')
    return input;

  const addKeyIfDefined = (targetObject: NullableFilterTarget, key: string, value: any): NullableFilterTarget =>
    value !== undefined ? { ...targetObject, [key]: value } : targetObject;

  const filterObjectKeys = (object: FilterTarget, keysTree: FilterTree) => {
    if (object === undefined || object === null)
      return object;

    return Object.keys(keysTree).reduce((obj: NullableFilterTarget, key: string) => addKeyIfDefined(
      obj,
      key,
      typeof keysTree[key] === 'object' && keysTree[key] !== null
        ? filterKeysRecursive((object as Map)[key], keysTree[key] as FilterTree)
        : keysTree[key] ? (object as Map)[key] : undefined,
    ), {} as FilterTarget);
  };

  return Array.isArray(input)
    ? input.map(item => filterObjectKeys(item, whitelistedKeysTree))
    : filterObjectKeys(input, whitelistedKeysTree);
}